import { useEffect, useRef, useState, type CSSProperties } from "react"
import { RouterProvider } from "react-router"
import { toast } from "sonner"
import {
    AUTH_SESSION_UPDATED_EVENT,
    readStoredAuthSession,
} from "./api/autoparts"
import { AppUpdateDialog } from "./components/app-update-dialog"
import { AssistantBot } from "./components/assistant-bot"
import { FloatingMessage } from "./components/floating-message"
import { FullscreenImageViewer } from "./components/fullscreen-image-viewer"
import { ImageUploadWebPDialog } from "./components/image-upload-webp-dialog"
import { Home } from "./components/icons"
import { Loading } from "./components/loading"
import { applyRouteSeoDefaults } from "./components/seo"
import { Toaster } from "./components/ui/sonner"
import { ThemeToggle } from "./components/theme/theme-toggle"
import {
    armMessageNotificationPermission,
    MESSAGE_NOTIFICATION_NAVIGATE_EVENT,
    type MessageNotificationCandidate,
    type MessageNotificationNavigationDetail,
} from "./lib/message-notifications"
import { preloadRouteModules, router } from "./routes.tsx"
import {
    BG_SCENES, CARD_TEXTURES, TEXTURE_IDS, sceneForPath,
    shouldLoadPhotos, unsplashUrl,
} from "./lib/backgrounds"

const NAVIGATION_PROGRESS_MIN_VISIBLE_MS = 320
const NAVIGATION_PROGRESS_HIDE_DELAY_MS = 120
const NAVIGATION_PROGRESS_EPSILON = 0.05

type NavigationProgressPhase = "submitting" | "loading"

const AUTHENTICATED_ROUTE_PREFIXES = [
    "/admin",
    "/buyer",
    "/courier",
    "/quality-assessor",
    "/seller",
] as const

function isAuthenticatedRoute(pathname: string): boolean {
    return AUTHENTICATED_ROUTE_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
}

function clamp(value: number, minimum: number, maximum: number): number {
    return Math.min(maximum, Math.max(minimum, value))
}

function getPhaseInitialTarget(phase: NavigationProgressPhase): number {
    return phase === "submitting" ? 10 : 16
}

function getPhaseCeiling(phase: NavigationProgressPhase): number {
    return phase === "submitting" ? 32 : 91
}

function getTimeBasedTarget(
    phase: NavigationProgressPhase,
    elapsedMs: number,
): number {
    const initial = getPhaseInitialTarget(phase)
    const ceiling = getPhaseCeiling(phase)
    const duration = phase === "submitting" ? 3200 : 5200

    return initial + (ceiling - initial) * (1 - Math.exp(-elapsedMs / duration))
}

function getInternalNavigationUrl(anchor: HTMLAnchorElement): URL | null {
    if (
        anchor.hasAttribute("download") ||
        (anchor.target && anchor.target !== "_self")
    ) {
        return null
    }

    try {
        const url = new URL(anchor.href, window.location.href)
        return url.origin === window.location.origin ? url : null
    } catch {
        return null
    }
}

function isDifferentRoute(url: URL): boolean {
    return url.pathname !== router.state.location.pathname
}

function GuestWelcomeButton() {
    return (
        <a
            href="/"
            aria-label="Back to welcome page"
            title="Back to welcome page"
            className="fixed bottom-3 right-3 z-layer-floating-priority flex h-12 w-12 items-center justify-center rounded-full border-2 border-accent bg-primary text-primary-foreground shadow-2xl shadow-black/20 ring-4 ring-accent/25 transition-transform hover:scale-105 hover:bg-primary/90 hover:ring-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 sm:bottom-7 sm:right-7"
        >
            <Home className="size-5" aria-hidden="true" />
        </a>
    )
}

function App() {
    const [photoPreferencesVersion, setPhotoPreferencesVersion] = useState(0)
    const viewportRef = useRef<HTMLDivElement>(null)
    const loadedSceneRef = useRef<string | null>(null)
    const [isInitialLoading, setIsInitialLoading] = useState(
        () => !router.state.initialized,
    )
    const [showNavigationIndicator, setShowNavigationIndicator] = useState(false)
    const [navigationProgress, setNavigationProgress] = useState(0)
    const [currentLocation, setCurrentLocation] = useState(() => ({
        pathname: router.state.location.pathname,
        search: router.state.location.search,
    }))
    const [isAuthenticated, setIsAuthenticated] = useState(
        () => Boolean(readStoredAuthSession()?.user),
    )
    const navigationActiveRef = useRef(false)
    const navigationStartedAtRef = useRef(0)
    const navigationPhaseRef = useRef<NavigationProgressPhase>("loading")
    const displayedProgressRef = useRef(0)
    const targetProgressRef = useRef(0)
    const animationFrameRef = useRef<number | null>(null)
    const lastAnimationAtRef = useRef(0)
    const completionTimerRef = useRef<number | null>(null)
    const hideTimerRef = useRef<number | null>(null)
    const navigationIntentTimerRef = useRef<number | null>(null)
    const completionRequestedRef = useRef(false)
    const initialPaintFramesRef = useRef(0)
    const authRedirectPendingRef = useRef(false)

    // Keep previously decoded photography visible while the next route/theme image loads.
    // Media/data-saver preference changes also clear photos immediately.
    useEffect(() => {
        const media = [
            '(prefers-reduced-data: reduce)',
            '(prefers-contrast: more)',
            '(forced-colors: active)',
        ].map((query) => window.matchMedia(query))
        const connection = (navigator as Navigator & {
            connection?: { addEventListener?: (name: string, cb: () => void) => void;
                removeEventListener?: (name: string, cb: () => void) => void }
        }).connection
        const refresh = () => setPhotoPreferencesVersion((value) => value + 1)
        media.forEach((item) => item.addEventListener('change', refresh))
        connection?.addEventListener?.('change', refresh)
        return () => {
            media.forEach((item) => item.removeEventListener('change', refresh))
            connection?.removeEventListener?.('change', refresh)
        }
    }, [])

    useEffect(() => {
        const viewport = viewportRef.current
        if (!viewport) return
        if (!shouldLoadPhotos()) {
            viewport.dataset.bgScene = 'none'
            viewport.dataset.bgReady = 'false'
            loadedSceneRef.current = null
            return
        }
        // Keep the same photograph in both themes. The theme switch changes
        // interface surfaces, not the photography or the page-wide tint.
        const scene = sceneForPath(currentLocation.pathname)
        const photo = BG_SCENES[scene].dark
        const key = scene
        if (loadedSceneRef.current === key) return
        let cancelled = false
        const width = window.innerWidth <= 768 ? 1280 : window.innerWidth >= 1600 ? 2560 : 1920
        const image = new Image()
        image.decoding = 'async'
        image.src = unsplashUrl(photo.id, { w: width, q: 72 })
        const showImage = () => {
            if (cancelled) return
            // All sizes are versions of the same image; set the CSS backgrounds
            // only after decoding the viewport-size request.
            for (const [suffix, pixels] of [['sm', 1280], ['md', 1920], ['lg', 2560]] as const) {
                viewport.style.setProperty(`--scene-image-${suffix}`, `url("${unsplashUrl(photo.id, { w: pixels, q: 72 })}")`)
            }
            viewport.style.setProperty('--scene-focal', photo.focal)
            viewport.dataset.bgScene = scene
            viewport.dataset.bgReady = 'true'
            loadedSceneRef.current = key
        }
        const failImage = () => {
            if (cancelled) return
            viewport.dataset.bgScene = 'none'
            viewport.dataset.bgReady = 'false'
            loadedSceneRef.current = null
        }
        image.onload = () => { void image.decode().then(showImage, showImage) }
        image.onerror = failImage
        if (image.complete && image.naturalWidth > 0) {
            void image.decode().then(showImage, showImage)
        }
        return () => { cancelled = true; image.onload = null; image.onerror = null }
    }, [currentLocation.pathname, photoPreferencesVersion])

    useEffect(() => {
        const syncAuthentication = () => {
            const hasAuthenticatedUser = Boolean(
                readStoredAuthSession()?.user,
            )
            setIsAuthenticated(hasAuthenticatedUser)

            if (hasAuthenticatedUser) {
                authRedirectPendingRef.current = false
                return
            }

            const pathname = router.state.location.pathname
            if (
                authRedirectPendingRef.current ||
                !isAuthenticatedRoute(pathname)
            ) {
                return
            }

            authRedirectPendingRef.current = true
            void router
                .navigate("/auth/login", { replace: true })
                .finally(() => {
                    authRedirectPendingRef.current = false
                })
        }

        window.addEventListener(AUTH_SESSION_UPDATED_EVENT, syncAuthentication)
        window.addEventListener("storage", syncAuthentication)

        return () => {
            window.removeEventListener(
                AUTH_SESSION_UPDATED_EVENT,
                syncAuthentication,
            )
            window.removeEventListener("storage", syncAuthentication)
        }
    }, [])

    useEffect(() => {
        const disarmNotifications = armMessageNotificationPermission()
        const handleIncomingMessage = (event: Event) => {
            const detail = (event as CustomEvent<MessageNotificationCandidate>)
                .detail
            if (!detail) return

            toast.info(
                detail.title ||
                    `New message from ${detail.senderName || "SpareLynk user"}`,
                {
                    description: detail.message,
                },
            )
        }
        const handleNotificationNavigation = (event: Event) => {
            const navigationEvent =
                event as CustomEvent<MessageNotificationNavigationDetail>
            const href = navigationEvent.detail?.href?.trim()
            if (!href) return

            let target: URL
            try {
                target = new URL(href, window.location.href)
            } catch {
                return
            }
            if (target.origin !== window.location.origin) return

            navigationEvent.preventDefault()
            void router.navigate(
                `${target.pathname}${target.search}${target.hash}`,
                { preventScrollReset: true },
            )
        }

        window.addEventListener("sparelynk:new-message", handleIncomingMessage)
        window.addEventListener(
            MESSAGE_NOTIFICATION_NAVIGATE_EVENT,
            handleNotificationNavigation,
        )

        return () => {
            disarmNotifications()
            window.removeEventListener(
                "sparelynk:new-message",
                handleIncomingMessage,
            )
            window.removeEventListener(
                MESSAGE_NOTIFICATION_NAVIGATE_EVENT,
                handleNotificationNavigation,
            )
        }
    }, [])

    useEffect(() => {
        applyRouteSeoDefaults(router.state.location.pathname, {
            resetPublicMetadata: false,
        })

        const clearCompletionTimer = () => {
            if (completionTimerRef.current !== null) {
                window.clearTimeout(completionTimerRef.current)
                completionTimerRef.current = null
            }
        }

        const clearHideTimer = () => {
            if (hideTimerRef.current !== null) {
                window.clearTimeout(hideTimerRef.current)
                hideTimerRef.current = null
            }
        }

        const clearNavigationIntentTimer = () => {
            if (navigationIntentTimerRef.current !== null) {
                window.clearTimeout(navigationIntentTimerRef.current)
                navigationIntentTimerRef.current = null
            }
        }

        const stopAnimation = () => {
            if (animationFrameRef.current !== null) {
                window.cancelAnimationFrame(animationFrameRef.current)
                animationFrameRef.current = null
            }
            lastAnimationAtRef.current = 0
        }

        const resetNavigationProgress = () => {
            stopAnimation()
            clearCompletionTimer()
            clearHideTimer()
            clearNavigationIntentTimer()
            navigationActiveRef.current = false
            completionRequestedRef.current = false
            displayedProgressRef.current = 0
            targetProgressRef.current = 0
            initialPaintFramesRef.current = 0
            setShowNavigationIndicator(false)
            setNavigationProgress(0)
        }

        const scheduleHide = () => {
            if (hideTimerRef.current !== null) return

            hideTimerRef.current = window.setTimeout(() => {
                hideTimerRef.current = null
                resetNavigationProgress()
            }, NAVIGATION_PROGRESS_HIDE_DELAY_MS)
        }

        const animateProgress = (timestamp: number) => {
            animationFrameRef.current = null

            if (!navigationActiveRef.current) return

            if (lastAnimationAtRef.current === 0) {
                lastAnimationAtRef.current = timestamp
            }

            const deltaSeconds = clamp(
                (timestamp - lastAnimationAtRef.current) / 1000,
                0,
                0.1,
            )
            lastAnimationAtRef.current = timestamp

            let effectiveTarget = targetProgressRef.current
            if (completionRequestedRef.current) {
                effectiveTarget = 100
            } else if (initialPaintFramesRef.current > 0) {
                initialPaintFramesRef.current -= 1
                effectiveTarget = 0
            } else {
                const elapsed = timestamp - navigationStartedAtRef.current
                effectiveTarget = Math.max(
                    effectiveTarget,
                    getTimeBasedTarget(navigationPhaseRef.current, elapsed),
                )
                effectiveTarget = Math.min(
                    effectiveTarget,
                    getPhaseCeiling(navigationPhaseRef.current),
                )
            }

            const current = displayedProgressRef.current
            const distance = Math.max(0, effectiveTarget - current)
            const speed = completionRequestedRef.current
                ? clamp(distance * 7, 180, 360)
                : clamp(distance * 2.4, 5, 46)
            const next = Math.min(effectiveTarget, current + speed * deltaSeconds)

            if (Math.abs(next - current) >= NAVIGATION_PROGRESS_EPSILON) {
                displayedProgressRef.current = next
                setNavigationProgress(next)
            }

            if (completionRequestedRef.current && next >= 99.9) {
                displayedProgressRef.current = 100
                setNavigationProgress(100)
                scheduleHide()
                return
            }

            animationFrameRef.current = window.requestAnimationFrame(
                animateProgress,
            )
        }

        const ensureAnimation = () => {
            if (animationFrameRef.current !== null) return
            animationFrameRef.current = window.requestAnimationFrame(
                animateProgress,
            )
        }

        const startNavigationProgress = (phase: NavigationProgressPhase) => {
            navigationPhaseRef.current = phase

            if (navigationActiveRef.current) {
                targetProgressRef.current = Math.max(
                    targetProgressRef.current,
                    getPhaseInitialTarget(phase),
                )
                ensureAnimation()
                return
            }

            clearCompletionTimer()
            clearHideTimer()
            stopAnimation()

            navigationActiveRef.current = true
            completionRequestedRef.current = false
            navigationStartedAtRef.current = performance.now()
            displayedProgressRef.current = 0
            targetProgressRef.current = getPhaseInitialTarget(phase)
            initialPaintFramesRef.current = 2
            setNavigationProgress(0)
            setShowNavigationIndicator(true)
            ensureAnimation()
        }

        const completeNavigationProgress = () => {
            if (
                !navigationActiveRef.current ||
                completionRequestedRef.current ||
                completionTimerRef.current !== null
            ) {
                return
            }

            const elapsed = performance.now() - navigationStartedAtRef.current
            const remainingVisibleTime = Math.max(
                0,
                NAVIGATION_PROGRESS_MIN_VISIBLE_MS - elapsed,
            )

            completionTimerRef.current = window.setTimeout(() => {
                completionTimerRef.current = null
                completionRequestedRef.current = true
                targetProgressRef.current = 100
                ensureAnimation()
            }, remainingVisibleTime)
        }

        const findAnchor = (event: Event): HTMLAnchorElement | null => {
            const target = event.target
            return target instanceof Element
                ? target.closest<HTMLAnchorElement>("a[href]")
                : null
        }

        const handleRoutePreload = (event: Event) => {
            const anchor = findAnchor(event)
            if (!anchor) return

            const url = getInternalNavigationUrl(anchor)
            if (!url || !isDifferentRoute(url)) return

            void preloadRouteModules(url.href).catch(() => undefined)
        }

        const handleNavigationIntent = (event: PointerEvent) => {
            if (
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return
            }

            const anchor = findAnchor(event)
            if (!anchor) return

            const url = getInternalNavigationUrl(anchor)
            if (!url || !isDifferentRoute(url)) return

            startNavigationProgress("loading")
            clearNavigationIntentTimer()
            navigationIntentTimerRef.current = window.setTimeout(() => {
                navigationIntentTimerRef.current = null
                const state = router.state
                if (state.navigation.state === "idle") {
                    completeNavigationProgress()
                }
            }, 900)
        }

        const unsubscribe = router.subscribe((state) => {
            setIsInitialLoading(!state.initialized)
            setCurrentLocation({
                pathname: state.location.pathname,
                search: state.location.search,
            })

            const navigationState = state.navigation.state
            const pendingLocation = state.navigation.location
            const isPathnameTransition =
                state.initialized &&
                navigationState !== "idle" &&
                pendingLocation !== undefined &&
                pendingLocation.pathname !== state.location.pathname

            if (isPathnameTransition) {
                clearNavigationIntentTimer()
                startNavigationProgress(
                    navigationState === "submitting" ? "submitting" : "loading",
                )
            } else if (navigationState === "idle") {
                completeNavigationProgress()
            }

            applyRouteSeoDefaults(state.location.pathname)
        })

        document.addEventListener("pointerover", handleRoutePreload, true)
        document.addEventListener("focusin", handleRoutePreload, true)
        document.addEventListener("touchstart", handleRoutePreload, {
            capture: true,
            passive: true,
        })
        document.addEventListener("pointerdown", handleNavigationIntent, true)

        return () => {
            unsubscribe()
            document.removeEventListener("pointerover", handleRoutePreload, true)
            document.removeEventListener("focusin", handleRoutePreload, true)
            document.removeEventListener("touchstart", handleRoutePreload, true)
            document.removeEventListener(
                "pointerdown",
                handleNavigationIntent,
                true,
            )
            resetNavigationProgress()
        }
    }, [])

    return (
        <>
            <div
                ref={viewportRef}
                className="app-viewport"
                data-bg-scene="none"
                data-bg-ready="false"
                style={Object.fromEntries(TEXTURE_IDS.map((id) => [
                    `--texture-${id}`, `url("${unsplashUrl(CARD_TEXTURES[id].id, { w: 900, q: 68 })}")`,
                ])) as CSSProperties}
            >
                <RouterProvider router={router} />
            </div>
            <AppUpdateDialog />
            {!isInitialLoading ? (
                <>
                    <AssistantBot
                        pathname={currentLocation.pathname}
                        search={currentLocation.search}
                    />
                    <FloatingMessage
                        pathname={currentLocation.pathname}
                        search={currentLocation.search}
                    />
                </>
            ) : null}

            {!isInitialLoading &&
            !isAuthenticated &&
            currentLocation.pathname !== "/" ? (
                <GuestWelcomeButton />
            ) : null}

            {isInitialLoading ? (
                <div className="fixed inset-0 z-layer-modal-content flex items-center justify-center bg-background">
                    <Loading />
                </div>
            ) : null}

            {showNavigationIndicator ? (
                <div
                    className="pointer-events-none fixed inset-x-0 top-0 z-layer-critical h-1 overflow-hidden bg-accent/15"
                    role="progressbar"
                    aria-label="Page loading progress"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(navigationProgress)}
                >
                    <div
                        className="h-full w-full origin-left bg-accent will-change-transform"
                        style={{
                            transform: `scaleX(${navigationProgress / 100})`,
                        }}
                    />
                </div>
            ) : null}

            <FullscreenImageViewer />
            <ImageUploadWebPDialog />
            <ThemeToggle />
            <Toaster closeButton position="top-right" />
        </>
    )
}

export default App
